import type { DATATYPES } from './contractXsd.js'
import type { DOSSIER_CONTROLS } from './field.js'

/**
 * Het kolomtype van een veld van het processysteem: het type van de kolom in Dataverse waar de waarde
 * staat (`koppelvlak.md` hoofdstuk 2, R-54, R-56, besluit van 27 oktober 2026).
 *
 * **Eén tabel, op één plek.** Uit het kolomtype volgt het datatype in het bericht en de control op het
 * formulier; de inrichter kiest de control niet zelf, behalve bij een keuze, waar hij tussen een
 * keuzelijst en keuzerondjes kiest. Dezelfde tabel leest de registry als hij het dossier-contract
 * toetst (`ContractRegistry`), de publicatiepoort als hij een geplaatst veld nagaat, de configurator
 * als hij een veld van het processysteem plaatst, en de documentatie. Een tweede lijst ernaast loopt
 * achter zodra er een kolomtype bijkomt.
 *
 * **De waarden zijn Nederlandse sleutels en blijven dat.** Ze staan in het contract dat het
 * processysteem aanlevert (`gc:kolomtype` in het XSD, `kolomtype` in de vorm van het model), dus een
 * andere naam is een ander dataformaat.
 */

type Datatype = (typeof DATATYPES)[number]
type DossierControl = (typeof DOSSIER_CONTROLS)[number]

/** Wat een kolomtype vastlegt. */
export interface KolomtypeRow {
  /** Hoe de configurator het kolomtype noemt, in de woorden van Dataverse. */
  readonly naam: string
  /** Het `datatype` van het element in het bericht en in het aanmaakverzoek (`koppelvlak.md` hoofdstuk 3). */
  readonly datatype: Datatype
  /** De control op het formulier; bij een keuze de twee waartussen de inrichter kiest, de eerste is de standaard. */
  readonly controls: readonly [DossierControl, ...DossierControl[]]
  /** Het JSON-type dat de eigenschap in het contract draagt. */
  readonly type: 'string' | 'integer' | 'number' | 'boolean'
  /** Het formaat dat erbij hoort, of geen. */
  readonly format?: 'date' | 'date-time'
  /**
   * Een formaat dat mag maar niet hoeft, omdat het kolomtype het al zegt: bij valuta het bedrag in euro
   * (R-54, besluit van 31 oktober 2026). Een dossier-contract van vóór dat besluit draagt het niet en
   * blijft geldig.
   */
  readonly formatOptional?: 'amount'
}

/**
 * De ondersteunde kolomtypes. Drie getaltypen van Dataverse (geheel getal, decimaal, drijvende komma)
 * worden alle drie een getal; het verschil is de opslag in Dataverse en niet wat de invuller ziet.
 */
export const KOLOMTYPE = {
  tekst: { naam: 'Tekst (één regel)', datatype: 'text', controls: ['text'], type: 'string' },
  email: { naam: 'E-mail', datatype: 'email', controls: ['email'], type: 'string' },
  url: { naam: 'URL', datatype: 'url', controls: ['url'], type: 'string' },
  telefoon: { naam: 'Telefoon', datatype: 'phone', controls: ['phone'], type: 'string' },
  richtext: { naam: 'Meerdere regels, rich text', datatype: 'richtext', controls: ['richtext'], type: 'string' },
  meerregelig: { naam: 'Meerdere regels, platte tekst', datatype: 'text', controls: ['textarea'], type: 'string' },
  'geheel-getal': { naam: 'Geheel getal', datatype: 'number', controls: ['number'], type: 'integer' },
  decimaal: { naam: 'Decimaal getal', datatype: 'number', controls: ['number'], type: 'number' },
  'drijvende-komma': { naam: 'Drijvende komma', datatype: 'number', controls: ['number'], type: 'number' },
  valuta: { naam: 'Valuta', datatype: 'amount', controls: ['currency'], type: 'number', formatOptional: 'amount' },
  datum: { naam: 'Alleen datum', datatype: 'date', controls: ['date'], type: 'string', format: 'date' },
  'datum-tijd': { naam: 'Datum en tijd', datatype: 'datetime', controls: ['datetime'], type: 'string', format: 'date-time' },
  keuze: { naam: 'Keuze', datatype: 'choice', controls: ['dropdown', 'radio'], type: 'string' },
  'ja-nee': { naam: 'Ja/nee', datatype: 'boolean', controls: ['toggle'], type: 'boolean' },
} as const satisfies Record<string, KolomtypeRow>

export type Kolomtype = keyof typeof KOLOMTYPE
export const KOLOMTYPES = Object.keys(KOLOMTYPE) as readonly Kolomtype[]

/**
 * Kolomtypes van Dataverse die een formulier niet kan dragen, met de reden. Ze hebben een sleutel zodat
 * de weigering kan zeggen wat er stond: een veld met zo'n kolomtype maakt het hele dossier-contract
 * ongeldig, want een veld dat stil wegvalt merkt pas iemand op als het eerste dossier wordt uitgezet.
 */
export const UNSUPPORTED_KOLOMTYPES = {
  'meerdere-keuzes': 'een keuze met meerdere waarden',
  zoeken: 'een verwijzing naar een andere tabel (lookup)',
  bestand: 'een bestand',
  afbeelding: 'een afbeelding',
  'automatisch-nummer': 'een nummer dat Dataverse zelf toekent',
  formule: 'een waarde die Dataverse zelf berekent',
} as const

export function isKolomtype(value: unknown): value is Kolomtype {
  return typeof value === 'string' && Object.hasOwn(KOLOMTYPE, value)
}

/** Het deel van een eigenschap in het dossier-contract waar deze toets naar kijkt. */
interface DossierProperty {
  readonly type?: unknown
  readonly format?: unknown
  readonly enum?: readonly unknown[]
  readonly kolomtype?: unknown
  readonly optielabels?: Readonly<Record<string, unknown>>
}

/** Een gat in het dossier-contract: welke eigenschap, en wat er mis is. */
export interface DossierContractProblem {
  readonly naam: string
  readonly wat: string
}

/**
 * Wat er niet klopt aan de velden van het dossier-contract (`ContractRegistry.dossier`).
 *
 * Elk veld draagt een ondersteund kolomtype, en het JSON-type en het formaat van de eigenschap passen
 * daarbij: een datum is tekst met formaat `date`, een geheel getal is `integer`. Alleen een keuze draagt
 * een vaste lijst waarden, en die is niet leeg en bevat alleen teksten: de sleutels uit Dataverse, zoals
 * `100000001`. Een label per optie hoort bij een sleutel uit die lijst.
 */
export function dossierContractProblems(properties: Readonly<Record<string, DossierProperty>>): DossierContractProblem[] {
  const problems: DossierContractProblem[] = []
  for (const [naam, property] of Object.entries(properties)) {
    const add = (wat: string) => problems.push({ naam, wat: `veld ${naam} van het processysteem: ${wat}` })
    const kolomtype = property.kolomtype
    if (kolomtype === undefined) {
      add('het kolomtype ontbreekt; elk veld in het dossier-contract noemt het type van zijn kolom in Dataverse')
      continue
    }
    if (typeof kolomtype === 'string' && Object.hasOwn(UNSUPPORTED_KOLOMTYPES, kolomtype)) {
      add(`kolomtype ${kolomtype} wordt niet ondersteund, want een formulier kan ${UNSUPPORTED_KOLOMTYPES[kolomtype as keyof typeof UNSUPPORTED_KOLOMTYPES]} niet tonen of aanvullen`)
      continue
    }
    if (!isKolomtype(kolomtype)) {
      add(`kolomtype ${String(kolomtype)} bestaat niet; verwacht een van ${KOLOMTYPES.join(', ')}`)
      continue
    }
    const row: KolomtypeRow = KOLOMTYPE[kolomtype]
    const types = Array.isArray(property.type) ? property.type : [property.type]
    if (types.length !== 1 || types[0] !== row.type) {
      add(`kolomtype ${kolomtype} verwacht type ${row.type}, het contract zegt ${types.map(String).join(' ')}`)
    }
    const optional = row.format === undefined && row.formatOptional !== undefined && property.format === row.formatOptional
    if (property.format !== row.format && !optional) {
      add(
        row.format === undefined
          ? `kolomtype ${kolomtype} heeft geen formaat, het contract zegt ${String(property.format)}`
          : `kolomtype ${kolomtype} verwacht formaat ${row.format}`,
      )
    }
    if (kolomtype === 'keuze') {
      const values = property.enum
      if (values === undefined || values.length === 0) add('een keuze noemt haar opties in een vaste lijst waarden (enum)')
      else if (!values.every((value) => typeof value === 'string' && value !== '')) add('de opties van een keuze zijn de sleutels uit Dataverse, als tekst')
      else if (new Set(values).size !== values.length) add('een sleutel staat twee keer in de opties')
    } else if (property.enum !== undefined) {
      add(`alleen een keuze heeft een vaste lijst waarden, niet kolomtype ${kolomtype}`)
    }
    for (const key of Object.keys(property.optielabels ?? {})) {
      if (kolomtype !== 'keuze') add(`alleen een keuze heeft labels per optie`)
      else if (!(property.enum ?? []).includes(key)) add(`een label voor optie ${key}, maar die sleutel staat niet in de opties`)
    }
  }
  return problems
}
